// ./src/components/features/Tasks/TasksFilterBar/TasksFilterBar.tsx
//
// Filter controls for the event Tasks / Appointments list: by task type
// (task / appointment — a segmented control), by lifecycle task-status
// (to do / in progress / paused / done — a compact CheckableMultiSelect, NOT a
// pill-growing MultiSelect), and by category (from the shared "task" vocabulary).
// Fully controlled — the page owns the value and maps it to a TaskFilter. An
// optional `leading` slot hosts the page's List/Calendar view toggle so it shares
// this row. Title/notes search lives in the page header, not here.

import React from 'react';
import { Group, SegmentedControl, Input, Button, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { TASK_TYPE_VALUES, TASK_STATUS_VALUES } from '@/utils/api';
import { AppIcon, CategorySelect, CheckableMultiSelect } from '@/components/generic';

import { getTaskTypeLabel, getTaskStatusLabel } from '../taskHelpers';

export interface TasksFilterValue {
  categoryId: string | null;
  taskType: string[];
  taskStatus: string[];
}

export interface TasksFilterBarProps {
  value: TasksFilterValue;
  onChange: (value: TasksFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  /** Optional control rendered at the start of the row (e.g. the view toggle). */
  leading?: React.ReactNode;
}

export const EMPTY_TASKS_FILTER: TasksFilterValue = {
  categoryId: null,
  taskType: [],
  taskStatus: [],
};

export const tasksFilterActiveCount = (value: TasksFilterValue): number =>
  (value.categoryId ? 1 : 0) + (value.taskType.length > 0 ? 1 : 0) + (value.taskStatus.length > 0 ? 1 : 0);

const isFilterActive = (value: TasksFilterValue): boolean => tasksFilterActiveCount(value) > 0;

const ANY = 'any';

const TasksFilterBarComponent: React.FC<TasksFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  leading,
}) => {
  const { t } = useTranslation(['events', 'common']);

  // 2 options → a segmented control (All / Tasks / Appointments), not a dropdown.
  const typeOptions = [
    { value: ANY, label: t('events:tasks.filters.type.any', { defaultValue: 'All' }) },
    ...TASK_TYPE_VALUES.map((type) => ({ value: type, label: getTaskTypeLabel(type) })),
  ];
  const taskStatusOptions = TASK_STATUS_VALUES.map((status) => ({
    value: status,
    label: getTaskStatusLabel(status),
  }));

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {leading}

      <Box>
        <Input.Wrapper label={t('events:tasks.filters.type.label', { defaultValue: 'Type' })} size="md">
          <Box>
            <SegmentedControl
              data={typeOptions}
              value={value.taskType[0] ?? ANY}
              onChange={(next) => onChange({ ...value, taskType: next === ANY ? [] : [next] })}
              disabled={disabled}
              size="md"
            />
          </Box>
        </Input.Wrapper>
      </Box>

      <Box style={{ width: 180 }}>
        <CheckableMultiSelect
          label={t('events:tasks.filters.taskStatus.label', { defaultValue: 'Progress' })}
          placeholder={t('events:tasks.filters.taskStatus.placeholder', { defaultValue: 'Any progress' })}
          data={taskStatusOptions}
          value={value.taskStatus}
          onChange={(taskStatus) => onChange({ ...value, taskStatus })}
          disabled={disabled}
          size="md"
        />
      </Box>

      <Box style={{ width: 200 }}>
        <CategorySelect
          group="task"
          label={t('events:tasks.filters.category.label', { defaultValue: 'Category' })}
          placeholder={t('events:tasks.filters.category.placeholder', { defaultValue: 'All categories' })}
          value={value.categoryId}
          onChange={(categoryId) => onChange({ ...value, categoryId })}
          disabled={disabled}
          clearable
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_TASKS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t('common:buttons.clear', { defaultValue: 'Clear' })}
        </Button>
      )}
    </Group>
  );
};

export const TasksFilterBar = React.memo(TasksFilterBarComponent);
